import { Box, HStack, Text } from "@chakra-ui/layout";
import { IconButton, Tooltip } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { Trans } from "@lingui/react";
import { captureEvent } from "@sentry/react";
import {
  Accounting_Account_Type,
  type BillingPlanBudgetInvoice,
  BillingPlanBudgetItemInvoiceTypeEnum,
  type BudgetItemTypeEnum,
  type ProjectBillingPlanSettingGenerateInvoiceItemsEnum,
  type WorkspaceAccountingAccount,
} from "@src/__generated__/graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  type IOption,
  Select,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { can } from "@src/utils/components/permissions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { toApiDate } from "@src/utils/dates";
import { currency, denominate } from "@src/utils/formatters";
import { gt, lt } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import type { DateRange } from "@src/utils/types";
import {
  addMonths,
  eachMonthOfInterval,
  endOfMonth,
  format,
  isSameMonth,
  startOfMonth,
  subMonths,
} from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import {
  Actions,
  BudgetFooter,
  BudgetItem,
  Day,
  DifferenceCell,
  DifferenceFooter,
  InvoicesPopover,
  MonthFooter,
  MonthInput,
  PlannedCell,
  PlannedFooter,
  Type,
} from "./components";
import { ProjectBillingPlan } from "./models/billing-plan";
import { ProjectBillingPlanSettings } from "./models/billing-plan-settings";
import type { ProjectBillingPlanRow } from "./models/row";
import type { ProjectBillingPlanSubRow } from "./models/subrow";

const MONTH_FORMAT = "MM/yyyy";

export type BillingPlanTableRow = {
  id: string;
  subRow: ProjectBillingPlanSubRow;
  parentRow: ProjectBillingPlanRow;
  isFirstSubRow: boolean;
};

type ConfirmModalAdditionalData = {
  onConfirm: () => void;
};

type EmailRecipientsUpdateAdditionalData = {
  onConfirm: () => void;
  onCancel: () => void;
  type: "billing-address" | "contact-person";
};

export class ProjectBillingPlanStore implements BaseStore {
  readonly tableKey = "project-billing-plan-table";
  appStore;

  confirmModalState = new DisclosureState<ConfirmModalAdditionalData>();
  billingEntityChangeConfirmState =
    new DisclosureState<EmailRecipientsUpdateAdditionalData>();

  @observable
  plan: ProjectBillingPlan;
  @observable
  settings: ProjectBillingPlanSettings;
  @observable
  projectId: string;
  daysOptions: IOption[];

  constructor(projectId: string, appStore: AppStore) {
    makeObservable(this);
    this.projectId = projectId;
    this.appStore = appStore;
    this.settings = new ProjectBillingPlanSettings(projectId, appStore);
    this.plan = new ProjectBillingPlan(projectId, this.settings, appStore);

    const options: IOption[] = [];
    for (let i = 1; i <= 31; i++) {
      options.push({
        value: i.toString(),
        label: i.toString(),
      });
    }
    this.daysOptions = options;

    this.refetch();
  }

  @action.bound
  async refetch() {
    this.settings.setNewLockedByUser(null);
    this.plan.fetchOptions();
    await this.settings.fetch();
    this.settings.fetcher.cacheResponse();
    await this.plan.fetch();
    this.plan.fetcher.cacheResponse();
  }

  @computed
  get canEditSettings(): boolean {
    if (!this.appStore.workspaceStore.settings?.automateInvoicingPlanApproval)
      return true;
    return can("project_accessAccountingAttributes");
  }

  @computed
  get settingsEditEnabled(): boolean {
    return this.canEditSettings && !this.settings.isPlanLocked;
  }

  @computed
  get isPlanDirty(): boolean {
    return (
      this.plan.data.some((row) => row.isDirty) ||
      !!this.settings.sendInvoicesForm?.isDirty ||
      !!this.settings.sendThankYouForm?.isDirty ||
      !!this.settings.issueInvoicesForm?.isDirty
    );
  }

  @computed
  get areMonthsDirty(): boolean {
    return this.plan.data.some((row) => row.areMonthsDirty);
  }

  @computed
  get isSaveDisabled(): boolean {
    return (
      this.plan.data.some((item) => item.hasError) ||
      this.settings.hasError ||
      this.settings.showLockedWarning
    );
  }

  @computed
  get issueInvoicesInputsDisabled(): boolean {
    if (!this.settings.issueInvoicesForm) return true;
    return (
      !this.settingsEditEnabled ||
      !this.settings.issueInvoicesForm.$.isChecked.value
    );
  }

  @computed
  get tableData(): BillingPlanTableRow[] {
    return this.plan.data.flatMap((parentRow) =>
      parentRow.__subRows.map((subRow, index) => ({
        id: subRow.id,
        subRow,
        parentRow,
        isFirstSubRow: index === 0,
      })),
    );
  }

  @computed
  get columns() {
    const rows = this.plan.data;
    const currencyCode = this.appStore.workspaceStore.settings?.currency.code;
    const currencyDenomination =
      this.appStore.workspaceStore.settings?.currency.denomination ?? 2;

    const cols: BodyColumnDef<BillingPlanTableRow>[] = [
      {
        id: "budget-item",
        meta: { label: t`Budget item` },
        header: t`Budget item`,
        cell: ({ row }) =>
          row.original.isFirstSubRow ? (
            <BudgetItem
              row={row.original.parentRow}
              showTitle={this.settings.showBudgetItems.value}
            />
          ) : row.original.parentRow.duplicateSubrowsIds.has(
              row.original.subRow.id,
            ) ? (
            <Tooltip
              hasArrow
              label={
                <Trans
                  id="invoice.conflict"
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  message="This budget item is already assigned to an invoice item scheduled for the {day, selectordinal,
                           one {#st}
                           two {#nd}
                           few {#rd}
                           other {#th}
                           } day. Multiple invoice items with the same invoicing date and type are not supported."
                  values={{ day: row.original.subRow.$.day.value }}
                />
              }
              placement="top"
            >
              <HStack align="center">
                <Icon name="alert-triangle" color="red" />

                <Text fontWeight="semibold" color="red">
                  {t`Duplicate`}
                </Text>
              </HStack>
            </Tooltip>
          ) : null,
        footer: () => (
          <Text fontWeight="bold" fontSize="sm">
            {t`Summary`}
          </Text>
        ),
      },
    ];

    if (this.settings.showBudgetItems.value) {
      cols.push({
        id: "type",
        meta: { label: t`Type` },
        header: t`Type`,
        cell: ({ row }) =>
          this.settings.isPlanLocked ? (
            <Text fontSize="sm">
              {row.original.subRow.$.type.value ===
              BillingPlanBudgetItemInvoiceTypeEnum.OutgoingInvoice
                ? t`Invoice`
                : t`Proforma`}
            </Text>
          ) : (
            <Type
              field={row.original.subRow.$.type}
              onChange={(value) => {
                row.original.subRow.$.type.onChange(value);
                this.changeUnassignedTypes(value);
              }}
            />
          ),
      });
    }

    cols.push({
      id: "day",
      meta: { label: t`Day` },
      header: t`Day`,
      cell: ({ row }) =>
        this.settings.isPlanLocked ? (
          <Text fontSize="sm">
            {row.original.subRow.$.day.value
              ? row.original.subRow.$.day.value.toString()
              : ""}
          </Text>
        ) : (
          <WrapComponent
            if={!this.settings.showAccountingColumns}
            with={(children) => <Box mr="2">{children}</Box>}
          >
            <Day
              field={row.original.subRow.$.day}
              dayOptions={this.daysOptions}
              onChange={(val) => {
                const value = val ? Number(val) : undefined;
                row.original.subRow.$.day.onChange(value);
                this.changeUnassignedDays(value);
              }}
            />
          </WrapComponent>
        ),
    });

    if (this.settings.showAccountingColumns) {
      cols.push(
        {
          id: "accounting-centre",
          meta: { label: t`Cost center` },
          header: t`Cost center`,
          cell: ({ row }) =>
            this.settings.isPlanLocked ? (
              <Text>
                {
                  this.plan.accountingCenterOptions.find(
                    (option) =>
                      option.value ===
                      row.original.subRow.$.accounting_center_id.value,
                  )?.label
                }
              </Text>
            ) : (
              <Box minW="150px">
                <Select
                  options={this.plan.accountingCenterOptions}
                  value={row.original.subRow.$.accounting_center_id.value}
                  isLoading={this.plan.isFetchingOptions}
                  onChange={(value) => {
                    row.original.subRow.$.accounting_center_id.onChange(value);
                    // this.changeUnassignedCostCenters(value);
                  }}
                  error={row.original.subRow.$.accounting_center_id.error}
                  menuMinWidth="170px"
                  asPortal
                  isClearable
                />
              </Box>
            ),
        },
        {
          id: "accounting-account",
          meta: { label: t`Accounting account` },
          header: t`Accounting account`,
          cell: ({ row }) => {
            const options = mapToOptions.accountingAccounts(
              this.getAccountingAccountsForCategoryOrUnassigned(
                row.original.parentRow.category.id,
                row.original.parentRow.budget_item_type,
              ),
            );

            return this.settings.isPlanLocked ? (
              <Text>
                {
                  options.find(
                    ({ value }) =>
                      value ===
                      row.original.subRow.$.accounting_account_id.value,
                  )?.label
                }
              </Text>
            ) : (
              <Box minW="150px" mr="2">
                <Select
                  options={options}
                  value={row.original.subRow.$.accounting_account_id.value}
                  onChange={(value) => {
                    row.original.subRow.$.accounting_account_id.onChange(value);
                    // this.changeUnassignedAccountingAccounts();
                  }}
                  error={row.original.subRow.$.accounting_account_id.error}
                  menuMinWidth="170px"
                  asPortal
                  isClearable
                />
              </Box>
            );
          },
        },
      );
    }

    cols.push(
      {
        id: "item-budget",
        meta: {
          label: t`Item budget`,
          align: "right",
          backgroundColor: "var(--chakra-colors-gray-100)",
        },
        header: t`Item budget`,
        cell: ({ row }) =>
          row.original.isFirstSubRow && (
            <Text fontSize="sm">
              {currency.formatByWorkspace(row.original.parentRow.total_budget)}
            </Text>
          ),
        footer: () => <BudgetFooter rows={rows} />,
      },
      {
        id: "planned",
        meta: {
          label: t`Planned`,
          align: "right",
          backgroundColor: "var(--chakra-colors-gray-100)",
        },
        header: t`Planned`,
        cell: ({ row }) =>
          row.original.isFirstSubRow ? (
            <PlannedCell row={row.original.parentRow} />
          ) : null,
        footer: () => <PlannedFooter rows={rows} />,
      },
      {
        id: "difference",
        meta: {
          label: t`Difference`,
          align: "right",
          backgroundColor: "var(--chakra-colors-gray-100)",
        },
        header: () => (
          <Text pr="2" fontWeight="inherit" fontSize="inherit">
            {t`Difference`}
          </Text>
        ),
        cell: ({ row }) =>
          row.original.isFirstSubRow ? (
            <DifferenceCell row={row.original.parentRow} />
          ) : null,
        footer: () => <DifferenceFooter rows={rows} />,
      },
    );

    if (isSameMonth(this.settings.period.start, this.settings.period.end)) {
      const label = format(
        startOfMonth(this.settings.period.start),
        MONTH_FORMAT,
      );

      cols.push({
        id: label,
        size: 150,
        minSize: 150,
        maxSize: 150,
        enableResizing: false,
        meta: {
          label: label,
          align: "right",
        },
        header: () => (
          <HStack alignItems="center">
            <InvoicesPopover
              invoices={this.getInvoicesForMonth(
                rows,
                this.settings.period.start,
              )}
            />
            <Text fontWeight="inherit">{label}</Text>
          </HStack>
        ),
        cell: ({ row }) =>
          this.settings.isPlanLocked ? (
            <Text fontSize="sm">
              {currency.formatByWorkspace(
                denominate(
                  Number(
                    row.original.subRow.getFieldByDate(
                      this.settings.period.start,
                    )?.value ?? "0",
                  ),
                  this.appStore.workspaceStore.settings?.currency
                    .denomination ?? 2,
                ),
              )}
            </Text>
          ) : (
            <MonthInput
              field={row.original.subRow.getFieldByDate(
                this.settings.period.start,
              )}
              currencyCode={currencyCode}
            />
          ),
        footer: () => (
          <MonthFooter
            rows={rows}
            month={this.settings.period.start}
            denomination={currencyDenomination}
          />
        ),
      });
    } else {
      eachMonthOfInterval(this.settings.period).forEach((month) => {
        const formattedMonth = format(month, MONTH_FORMAT);
        const invoices = this.getInvoicesForMonth(rows, month);

        cols.push({
          id: formattedMonth,
          enableResizing: false,
          minSize: 150,
          meta: { label: formattedMonth, align: "right" },
          header: () => (
            <WrapComponent
              if={
                isSameMonth(this.settings.period.end, month) &&
                !this.settings.isPlanLocked
              }
              with={(children) => (
                <HStack spacing="2" justifyContent="end">
                  {children}
                  <Tooltip label={t`Remove month`} placement="top">
                    <IconButton
                      aria-label={t`Month removal button`}
                      colorScheme="gray"
                      icon={<Icon name="trash-03" />}
                      onClick={() => {
                        this.handleRemoveMonth(toApiDate(month));
                      }}
                      size="sm"
                      variant="ghost"
                    />
                  </Tooltip>
                </HStack>
              )}
            >
              <HStack alignItems="center" justifyContent="end">
                <InvoicesPopover invoices={invoices} />
                <Text fontWeight="inherit">{formattedMonth}</Text>
              </HStack>
            </WrapComponent>
          ),
          cell: ({ row }) =>
            this.settings.isPlanLocked ? (
              <Text fontSize="sm">
                {currency.formatByWorkspace(
                  denominate(
                    Number(
                      row.original.subRow.getFieldByDate(month)?.value ?? "0",
                    ),
                    this.appStore.workspaceStore.settings?.currency
                      .denomination ?? 2,
                  ),
                )}
              </Text>
            ) : (
              <MonthInput
                field={row.original.subRow.getFieldByDate(month)}
                currencyCode={currencyCode}
              />
            ),
          footer: () => (
            <MonthFooter
              rows={rows}
              month={month}
              denomination={currencyDenomination}
            />
          ),
        });
      });
    }

    cols.push({
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
      header: t`Actions`,
      size: 140,
      cell: ({ row }) =>
        this.settings.isPlanLocked ? null : (
          <Actions
            row={row.original.parentRow}
            subRow={row.original.subRow}
            period={this.settings.period}
          />
        ),
    });

    return cols;
  }

  @action.bound
  changeUnassignedDays(value: number | undefined) {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        if (subRow.$.day.$) continue;
        subRow.$.day.onChange(value);
      }
    }
  }

  @action.bound
  changeUnassignedTypes(
    value: BillingPlanBudgetItemInvoiceTypeEnum | undefined,
  ) {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        if (subRow.$.type.$) continue;
        subRow.$.type.onChange(value);
      }
    }
  }

  @action.bound
  changeUnassignedCostCenters(value: string | undefined) {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        if (subRow.$.accounting_center_id.$) continue;
        subRow.$.accounting_center_id.onChange(value);
      }
    }
  }

  @action.bound
  changeUnassignedAccountingAccounts() {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        if (subRow.$.accounting_account_id.$) continue;
        const accountingAccounts =
          this.getAccountingAccountsForCategoryOrUnassigned(
            row.category.id,
            row.budget_item_type,
          );
        if (!accountingAccounts.length) continue;
        const accountingAccountWithSameCategory = accountingAccounts.find(
          ({ budget_categories }) =>
            budget_categories?.some(
              ({ budget_category_id }) =>
                budget_category_id === row.category.id,
            ),
        );
        subRow.$.accounting_account_id.onChange(
          accountingAccountWithSameCategory?.id ?? accountingAccounts[0].id,
        );
      }
    }
  }

  @action.bound
  private onShowBudgetItemsChange(isChecked?: boolean) {
    if (isChecked) {
      this.settings.showBudgetItems.on();
    } else {
      this.settings.showBudgetItems.off();
    }

    if (
      isChecked === false ||
      (!this.settings.showBudgetItems.value && isChecked === undefined)
    ) {
      this.settings.issueInvoicesForm?.$.isChecked.reset(false);
      this.settings.sendInvoicesForm?.sendEmail.reset(false);
      this.settings.sendThankYouForm?.sendEmail.reset(false);
      this.clearAccountingColumns();
    }

    this.plan.fetch();
  }

  @action.bound
  clearAccountingColumns() {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        subRow.$.accounting_center_id.onChange(undefined);
        subRow.$.accounting_account_id.onChange(undefined);
      }
    }
  }

  @action.bound
  handleInvoiceItemsChange(
    value: ProjectBillingPlanSettingGenerateInvoiceItemsEnum | undefined,
  ) {
    this.settings.issueInvoicesForm?.$.generateInvoiceItems.onChange(value);

    this.clearAccountingColumns();
  }

  @action.bound
  handleShowBudgetItems(isChecked: boolean) {
    if (this.isPlanDirty) {
      this.confirmModalState.onOpen({
        onConfirm: () => {
          this.onShowBudgetItemsChange(isChecked);
        },
      });
      return;
    }

    this.onShowBudgetItemsChange(isChecked);
  }

  @action.bound
  handleAddMonth() {
    const newMonth = endOfMonth(addMonths(this.settings.period.end, 1));
    this.settings.setPeriod({
      start: this.settings.period.start,
      end: newMonth,
    });

    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        subRow.setMonth(newMonth, 0);
      }
    }
  }

  @action.bound
  handleRemoveMonth(month: string) {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        subRow.$.months.$.delete(month);
      }
    }

    this.settings.setPeriod({
      start: this.settings.period.start,
      end: endOfMonth(subMonths(this.settings.period.end, 1)),
    });
  }

  @action.bound
  handleSetMonths(period: DateRange) {
    for (const row of this.plan.data) {
      for (const subRow of row.__subRows) {
        subRow.setMonths(
          eachMonthOfInterval(period).map((month) => ({
            date: toApiDate(month),
            value: 0,
          })),
        );
      }
    }
  }

  @action.bound
  handleModalConfirm() {
    if (!this.confirmModalState.additionalData) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong please try again later.`,
      });
      captureEvent({
        message:
          "FE: No additionalData in confirmModalState while opening modal.",
      });
      return;
    }

    this.confirmModalState.additionalData.onConfirm();
  }

  @action.bound
  handleBillingEntityChange(value: string | undefined) {
    const oldBillingEntityId =
      this.settings.issueInvoicesForm?.$.billing_entity_id.value;

    this.settings.issueInvoicesForm?.handleBillingEntityChange(value);

    const shouldUpdateRecipients =
      this.settings.sendInvoicesForm?.sendEmail.$ ||
      this.settings.sendThankYouForm?.sendEmail.$;

    if (!shouldUpdateRecipients) return;

    this.billingEntityChangeConfirmState.onOpen({
      onConfirm: () => {
        this.settings.updateEmailTemplates();
      },
      onCancel: () => {
        if (oldBillingEntityId) {
          this.settings.issueInvoicesForm?.handleBillingEntityChange(
            oldBillingEntityId,
          );
        } else {
          this.settings.issueInvoicesForm?.$.billing_entity_id.onChange("");
        }
      },
      type: "billing-address",
    });
  }

  @action.bound
  handleContactChange(value: string | undefined) {
    const oldContactId = this.settings.issueInvoicesForm?.$.contact_id.value;
    const oldContactEmail =
      this.settings.issueInvoicesForm?.selectedContactEmail;

    this.settings.issueInvoicesForm?.$.contact_id.reset(value);

    const newContactEmail =
      this.settings.issueInvoicesForm?.selectedContactEmail;

    const shouldUpdateRecipients =
      this.settings.sendInvoicesForm?.sendEmail.$ ||
      this.settings.sendThankYouForm?.sendEmail.$;

    if (!shouldUpdateRecipients) return;

    this.billingEntityChangeConfirmState.onClose();

    this.billingEntityChangeConfirmState.onOpen({
      onConfirm: () => {
        this.settings.replaceContactEmailInRecipients(
          oldContactEmail,
          newContactEmail,
        );
      },
      onCancel: () => {
        this.settings.issueInvoicesForm?.$.contact_id.reset(oldContactId);
      },
      type: "contact-person",
    });
  }

  @action.bound
  handleBillingEntityChangeConfirm() {
    if (!this.billingEntityChangeConfirmState.additionalData) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong please try again later.`,
      });
      captureEvent({
        message:
          "FE: No additionalData in billingEntityChangeConfirmState while confirming.",
      });
      return;
    }

    this.billingEntityChangeConfirmState.additionalData.onConfirm();
    this.billingEntityChangeConfirmState.onClose();
  }

  @action.bound
  handleBillingEntityChangeModalClose() {
    this.billingEntityChangeConfirmState.additionalData?.onCancel?.();
    this.billingEntityChangeConfirmState.onClose();
  }

  @action.bound
  handleCancel() {
    this.settings.revertToCached();
    this.plan.revertToCached();
  }

  @action.bound
  async handleSave() {
    let hasError = false;

    for (const { __subRows } of this.plan.data) {
      for (const row of __subRows) {
        if (this.settings.showBudgetItems.value) {
          row.$.type.validators(row.requiredIfMonthsArentEmpty);
          row.$.day.validators(gt(0), lt(32), row.requiredIfMonthsArentEmpty);
        } else {
          row.$.type.validators();
          row.$.day.validators(gt(0), lt(32));
        }
        const validation = await row.validate();
        if (!validation.hasError) continue;
        hasError = true;
      }
    }

    await this.settings.issueInvoicesForm?.validate();
    await this.settings.sendInvoicesForm?.validate();
    await this.settings.sendThankYouForm?.validate();

    if (this.settings.issueInvoicesForm?.formHasError) {
      hasError = true;
    }
    if (this.settings.sendInvoicesForm?.formHasError) {
      hasError = true;
    }
    if (this.settings.sendThankYouForm?.formHasError) {
      hasError = true;
    }

    if (hasError) return;

    if (this.canEditSettings) {
      await this.settings.update();
    }

    await this.plan.update();
    this.appStore.UIStore.toast({
      status: "success",
      title: t`Saved successfully.`,
    });
  }

  @action.bound
  async handleLockPlan() {
    if (!this.settings.isPlanLocked) {
      await this.handleSave();
    }
    await this.settings.lockPlan();
  }

  getInvoicesForMonth(
    rows: ProjectBillingPlanRow[],
    month: Date,
  ): BillingPlanBudgetInvoice[] {
    const invoices = new Map<string, BillingPlanBudgetInvoice>();

    for (const row of rows) {
      for (const subrow of row.__subRows) {
        if (!subrow) continue;
        for (const invoice of subrow.invoices) {
          if (!invoice || !isSameMonth(invoice.date, month)) continue;
          invoices.set(
            `${invoice.invoice_id}-${invoice.invoice_type}`,
            invoice,
          );
        }
      }
    }

    return Array.from(invoices.values());
  }

  getAccountingAccountsForCategoryOrUnassigned(
    categoryId: string,
    budgetItemType: BudgetItemTypeEnum,
  ): WorkspaceAccountingAccount[] {
    return this.appStore.workspaceStore.accountingAccounts
      .filter(
        ({ accounting_type }) =>
          accounting_type === Accounting_Account_Type.Invoice,
      )
      .filter(
        ({ budget_categories }) =>
          !budget_categories?.length ||
          budget_categories.some(
            ({ budget_category_id, budget_category_type }) =>
              budget_category_id === categoryId &&
              budget_category_type === budgetItemType,
          ),
      );
  }
}
